// Lightweight URL state — region, year, eraId encoded as query params
import { useEffect, useState, useCallback } from 'react'

export type AtlasView = 'help'

export interface UrlState {
  region: string | null
  year: number | null
  era: string | null
  figure: string | null
  view: AtlasView | null     // null = main atlas; 'help' = how-to-use page
}

function parseView(raw: string | null): AtlasView | null {
  return raw === 'help' ? 'help' : null
}

function parse(): UrlState {
  if (typeof window === 'undefined') {
    return { region: null, year: null, era: null, figure: null, view: null }
  }
  const p = new URLSearchParams(window.location.search)
  const y = p.get('year')
  return {
    region: p.get('region'),
    year:   y === null ? null : Number(y),
    era:    p.get('era'),
    figure: p.get('figure'),
    view:   parseView(p.get('view'))
  }
}

function write(state: UrlState) {
  const p = new URLSearchParams()
  if (state.region) p.set('region', state.region)
  if (state.year !== null && state.year !== undefined) p.set('year', String(state.year))
  if (state.era) p.set('era', state.era)
  if (state.figure) p.set('figure', state.figure)
  if (state.view) p.set('view', state.view)
  const qs = p.toString()
  const url = qs ? `?${qs}` : window.location.pathname
  window.history.replaceState(null, '', url)
}

export function useUrlState() {
  const [state, setState] = useState<UrlState>(() => parse())
  const update = useCallback((patch: Partial<UrlState>) => {
    setState(prev => {
      const next = { ...prev, ...patch }
      write(next)
      return next
    })
  }, [])
  useEffect(() => {
    const onPop = () => setState(parse())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  return [state, update] as const
}
